import { useCallback, useState } from 'react';
import { FinanceModelTypes, PeripheralFinanceModelTypes } from '../api/contracts/financemodels';
import {
  useFinanceModels,
  usePeripheralFinanceModels,
  useUpdateFinanceModels,
  useUpdatePeripheralFinanceModels
} from '../api/hooks/financemodels';

export interface DefaultFinanceModelOption {
  type: FinanceModelTypes | PeripheralFinanceModelTypes;
  term?: number;
  name: string;
}

export interface DefaultFinanceModelData {
  categoryId: string;
  categoryName: string;
  selectedDefault?: DefaultFinanceModelOption;
  options: DefaultFinanceModelOption[];
}

export const useDefaultFinanceModels = () => {
  const models = useFinanceModels();
  const perModels = usePeripheralFinanceModels();

  const [defaults, setDefaults] = useState<DefaultFinanceModelData[]>(() => {
    const defaultsData: DefaultFinanceModelData[] = [];

    models.forEach(model => {
      let defaultData = defaultsData.find(d => d.categoryId === model.categoryId);

      if (!defaultData) {
        defaultData = {
          categoryId: model.categoryId,
          categoryName: model.category.name,
          options: []
        };
        defaultsData.push(defaultData);
      }

      const option = {
        type: model.financeModelType,
        term: model.term,
        name: model.name
      };

      if (!defaultData.options.find(o => o.type === option.type && o.term === option.term)) {
        defaultData.options.push(option);
      }

      if (!defaultData.selectedDefault && model.isDefault) {
        defaultData.selectedDefault = option;
      }
    });

    perModels.forEach(model => {
      let defaultData = defaultsData.find(d => !d.categoryId);

      if (!defaultData) {
        defaultData = {
          categoryId: '',
          options: [],
          categoryName: 'Peripherals'
        };
        defaultsData.push(defaultData);
      }

      const option = {
        type: model.peripheralFinanceModelType,
        term: model.term,
        name: model.name
      };

      if (!defaultData.options.find(o => o.type === option.type && o.term === option.term)) {
        defaultData.options.push(option);
      }

      if (!defaultData.selectedDefault && model.isDefault) {
        defaultData.selectedDefault = option;
      }
    });

    defaultsData.forEach(d => {
      d.options.sort((a, b) => {
        if (a.type > b.type) {
          return 1;
        } else if (b.type > a.type) {
          return -1;
        } else {
          return (a.term || 0) - (b.term || 0);
        }
      });
    });

    return defaultsData;
  });

  const setDefaultModel = (categoryId: string, option?: DefaultFinanceModelOption) => {
    const newDefaults = [...defaults];

    const defaultData = newDefaults.find(d => d.categoryId === categoryId);

    if (!defaultData) {
      throw Error('Attempting to set data for undefined default');
    }

    if (option) {
      defaultData.selectedDefault = option;
    } else {
      delete defaultData.selectedDefault;
    }

    setDefaults(newDefaults);
  };

  return { defaults, setDefaultModel };
};

export const useSaveDefaultFinanceModels = () => {
  const models = useFinanceModels();
  const perModels = usePeripheralFinanceModels();

  const updateFinanceModels = useUpdateFinanceModels();
  const updatePeripheralFinanceModels = useUpdatePeripheralFinanceModels();

  return useCallback(
    async (defaultsData: DefaultFinanceModelData[]) => {
      const mainDevicePayload = models.map(model => {
        const defaultData = defaultsData.find(d => d.categoryId === model.categoryId);
        const selectedDefault = defaultData?.selectedDefault;
        const isDefault = selectedDefault?.type === model.financeModelType && selectedDefault?.term === model.term;

        return {
          categoryId: model.categoryId,
          manufacturerId: model.manufacturerId,
          regionId: model.regionId,
          term: model.term,
          value: model.value,
          pcmFactor: model.pcmFactor,
          type: model.financeModelType,
          isDefault
        };
      });

      const peripheralFinanceModelsPayload = perModels.map(model => {
        const defaultData = defaultsData.find(d => d.categoryId === '');
        const selectedDefault = defaultData?.selectedDefault;
        const isDefault =
          selectedDefault?.type === model.peripheralFinanceModelType && selectedDefault?.term === model.term;

        return {
          regionId: model.regionId,
          term: model.term,
          value: model.value,
          pcmFactor: model.pcmFactor,
          type: model.peripheralFinanceModelType,
          isDefault
        };
      });

      await updateFinanceModels.mutateAsync(mainDevicePayload);
      await updatePeripheralFinanceModels.mutateAsync(peripheralFinanceModelsPayload);
    },
    [models, perModels]
  );
};
